<template>
  <div class="orcamentos-page" data-testid="orcamentos-page">
    <!-- Orcamentos Table -->
    <div id="orcamentosPrincipal" class="table-responsive">
      <orcamentos-table
        ref="orcamentosTableRef"
        @edit-orcamento="handleEditOrcamento"
        @cliente.edit="handleEditCliente"
        @schedule-orcamento="handleScheduleOrcamento"
      />
    </div>

    <!-- OrcamentosModal is centralized in ModalManager.vue - opened via eventBus -->
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount } from 'vue';
import { log } from '../../utils/DebugLogger';
import { eventBus } from '../../eventBus';
import OrcamentosTable from './OrcamentosTable.vue';
// OrcamentosModal is centralized in ModalManager.vue

type Id = number | string;

interface OrcamentosTableInstance {
  loadData: () => void;
}

// Refs
const orcamentosTableRef = ref<OrcamentosTableInstance | null>(null);

// Methods
const loadData = (): void => {
  log('OrcamentosPage', 'loadData', 'Loading data');

  if (orcamentosTableRef.value) {
    orcamentosTableRef.value.loadData();
  }
};

const openNewModal = (): void => {
  log('OrcamentosPage', 'openNewModal', 'Opening modal for new orcamento via eventBus');
  eventBus.emit('modal.orcamentos.open', { action: 'new' });
};

const handleEditOrcamento = (orcamentoId: Id): void => {
  log('OrcamentosPage', 'handleEditOrcamento', 'Editing orcamento via eventBus', orcamentoId);
  eventBus.emit('modal.orcamentos.open', { id: orcamentoId });
};

const handleEditCliente = (clienteId: Id): void => {
  log('OrcamentosPage', 'handleEditCliente', 'Editing cliente via eventBus', clienteId);
  eventBus.emit('modal.cliente.open', { id: clienteId });
};

const handleScheduleOrcamento = (orcamentoId: Id): void => {
  log('OrcamentosPage', 'handleScheduleOrcamento', 'Scheduling orcamento', orcamentoId);
  // This is handled in the table component with redirect to agenda
};

const handleNewOrcamentoForClient = (clienteId: Id): void => {
  log('OrcamentosPage', 'handleNewOrcamentoForClient', 'New orcamento for client via eventBus', clienteId);
  eventBus.emit('modal.orcamentos.open', { clienteId: clienteId });
};

// Stable handler references for proper cleanup
const handleOpenNewModalEvent = (): void => {
  log('OrcamentosPage', 'event:orcamento.openNewModal', 'Received');
  openNewModal();
};

const handleEditRequestedEvent = (orcamentoId?: Id): void => {
  log('OrcamentosPage', 'event:orcamento.editRequested', 'Received', orcamentoId);
  if (orcamentoId !== undefined) handleEditOrcamento(orcamentoId);
};

const handleNewForClientRequestedEvent = (clienteId?: Id): void => {
  log('OrcamentosPage', 'event:orcamento.newForClientRequested', 'Received', clienteId);
  if (clienteId !== undefined) handleNewOrcamentoForClient(clienteId);
};

const handleFilterUpdatedEvent = (): void => {
  log('OrcamentosPage', 'event:filter.ready', 'Received');
  loadData();
};

const handleOrcamentoSavedEvent = (): void => {
  log('OrcamentosPage', 'event:orcamento.saved', 'Received');
  loadData();
};

// Event-based communication setup for backward compatibility
const setupEventListeners = (): void => {
  log('OrcamentosPage', 'setupEventListeners', 'Setting up event listeners');

  eventBus.on('orcamento.openNewModal', handleOpenNewModalEvent);
  eventBus.on<Id>('orcamento.editRequested', handleEditRequestedEvent);
  eventBus.on<Id>('orcamento.newForClientRequested', handleNewForClientRequestedEvent);
};

const cleanupEventListeners = (): void => {
  log('OrcamentosPage', 'cleanupEventListeners', 'Cleaning up event listeners');

  eventBus.off('orcamento.openNewModal', handleOpenNewModalEvent);
  eventBus.off<Id>('orcamento.editRequested', handleEditRequestedEvent);
  eventBus.off<Id>('orcamento.newForClientRequested', handleNewForClientRequestedEvent);
};

// Lifecycle
onMounted(() => {
  log('OrcamentosPage', 'mounted', 'Component mounted');

  setupEventListeners();

  // Don't load data here - let OrcamentosTable handle it
  // loadData(); // REMOVED - OrcamentosTable handles initial load

  // Listen for filter ready event
  eventBus.on('filter.updated', handleFilterUpdatedEvent);
  eventBus.on('orcamento.saved', handleOrcamentoSavedEvent);
});

onBeforeUnmount(() => {
  log('OrcamentosPage', 'beforeUnmount', 'Component unmounting');

  cleanupEventListeners();

  // Clean up event listeners
  eventBus.off('filter.updated', handleFilterUpdatedEvent);
  eventBus.off('orcamento.saved', handleOrcamentoSavedEvent);
});

// Expose methods for external access
defineExpose({
  loadData,
  openNewModal,
  handleEditOrcamento,
  handleEditCliente,
  handleNewOrcamentoForClient
});
</script>

<style scoped>
/* Page-specific styles can be added here */
</style>

